import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  CircularProgress, 
  Alert, 
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Divider
} from '@mui/material';
import { Search, Clear, ArrowUpward, ArrowDownward, Refresh } from '@mui/icons-material';
import apiService from '../../services/apiService';
import '../styles/ContentComponents.css';

function Organizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // Search and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('create_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [minSessionCount, setMinSessionCount] = useState(50);
  const [appliedMinSessionCount, setAppliedMinSessionCount] = useState(50);
  
  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    departments: 0
  });

  useEffect(() => {
    fetchOrganizations();
  }, [
    pagination.page,
    pagination.limit,
    appliedSearchTerm,
    sortBy,
    sortOrder,
    appliedMinSessionCount,
  ]);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      search: appliedSearchTerm || null,
      sort_by: sortBy,
      sort_order: sortOrder,
      min_session_count: appliedMinSessionCount,
    };
    
    const result = await apiService.fetchOrganizationsPaginated(params);
    
    if (result.success) {
      const { organizations: orgsData, pagination: paginationData } = result.data;
      
      setOrganizations(orgsData);
      setPagination(prev => ({
        ...prev,
        page: paginationData.page,
        limit: paginationData.limit,
        totalCount: paginationData.total_count,
        totalPages: paginationData.total_pages,
        hasNext: paginationData.has_next,
        hasPrev: paginationData.has_prev
      }));
      
      // Calculate statistics from current page data
      // Note: For accurate stats, we'd need a separate endpoint for statistics
      const active = orgsData.length; // All organizations are considered active if they exist
      const departments = orgsData.filter(org => 
        org.industry === 'department' || org.organization_name?.toLowerCase().includes('department')
      ).length;
      
      setStats({ 
        total: paginationData.total_count,
        active, 
        departments 
      });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleRetry = () => {
    fetchOrganizations();
  };

  const handleRowClick = (organization) => {
    navigate(`/project/${organization.unique_id}`);
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleMinSessionCountChange = (event) => {
    const raw = event.target.value;
    if (raw === '') {
      setMinSessionCount(0);
      return;
    }

    const parsed = parseInt(raw, 10);
    setMinSessionCount(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
  };

  const applySearch = () => {
    setAppliedSearchTerm(searchTerm);
    setAppliedMinSessionCount(minSessionCount);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setMinSessionCount(0);
    setAppliedMinSessionCount(0);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      applySearch();
    }
  };

  // Sort handlers
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when sorting
  };

  const getSetupStatus = (needSetup) => {
    return needSetup ? 'Setup Required' : 'Setup Complete';
  };

  const getDemoStatus = (demo) => {
    return demo ? 'Demo' : 'Production';
  };

  const truncateToken = (token) => {
    if (!token) return 'No token';
    return token.length > 12 ? `${token.slice(0, 12)}...` : token;
  };

  const getIndustryDisplay = (industry) => {
    if (!industry) return 'Not specified';
    return industry.charAt(0).toUpperCase() + industry.slice(1);
  };

  // Define table columns
  const columns = [
    {
      key: 'unique_id',
      label: 'Project ID',
      type: 'truncate',
      maxLength: 20
    },
    {
      key: 'organization_name',
      label: 'Name',
      render: (value) => value || 'Unnamed'
    },
    {
      key: 'token',
      label: 'Token',
      render: (value) => (
        <span title={value}>
          {truncateToken(value)}
        </span>
      )
    },
    {
      key: 'company_name',
      label: 'Company',
      render: (value, item) => {
        const name = value == null ? '' : String(value).trim();
        if (name) return name;
        if (item.company_id != null && item.company_id !== '')
          return String(item.company_id);
        return 'N/A';
      }
    },
    {
      key: 'industry',
      label: 'Industry',
      render: (value) => getIndustryDisplay(value)
    },
    {
      key: 'need_setup',
      label: 'Setup Status',
      render: (value) => (
        <Chip 
          label={getSetupStatus(value)} 
          color={value ? 'warning' : 'success'} 
          size="small" 
          variant="outlined"
        />
      )
    },
    {
      key: 'demo',
      label: 'Demo Status',
      render: (value) => (
        <Chip 
          label={getDemoStatus(value)} 
          color={value ? 'info' : 'primary'} 
          size="small" 
          variant="outlined"
        />
      )
    },
    {
      key: 'session_count',
      label: 'Session Count',
      render: (value) => {
        const count = value != null ? value : 0;
        return new Intl.NumberFormat().format(count);
      }
    },
    {
      key: 'create_date',
      label: 'Created Date',
      type: 'date',
      render: (value) => value ? apiService.formatDate(value) : 'N/A'
    }
  ];

  // Render cell content based on column type
  const renderCell = (item, column) => {
    const value = item[column.key];
    
    if (column.render) {
      return column.render(value, item);
    }
    
    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : 'N/A';
    }
    
    if (column.type === 'truncate' && value && value.length > column.maxLength) {
      return (
        <span title={value}>
          {value.slice(0, column.maxLength)}...
        </span>
      );
    }
    
    return value || 'N/A';
  };

  // Loading skeleton for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.limit }).map((_, index) => (
        <TableRow key={index}>
          {columns.map((column) => (
            <TableCell key={column.key} sx={{ py: 0.5 }}>
              <Skeleton variant="text" width="80%" height={16} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <Box sx={{ 
      height: '100vh',
      maxHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'grey.50'
    }}>
      {/* Header Section */}
      <Paper elevation={1} sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
            🏛️ Projects Management
          </Typography>
        </Box>
        
        {/* Search and Controls */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search projects by name, industry, or description..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton size="small" onClick={clearSearch}>
                        <Clear />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              variant="outlined"
              label="Min sessions"
              type="number"
              value={minSessionCount}
              onChange={handleMinSessionCountChange}
              onKeyPress={handleSearchKeyPress}
              inputProps={{ min: 0 }}
              sx={{ width: 140 }}
            />
            <Button
              variant="contained"
              onClick={applySearch}
              disabled={loading}
              size="small"
              sx={{ minWidth: 80 }}
            >
              Search
            </Button>
          </Box>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => {
                setSortBy(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <MenuItem value="create_date">Created Date</MenuItem>
              <MenuItem value="organization_name">Name</MenuItem>
              <MenuItem value="industry">Industry</MenuItem>
              <MenuItem value="company_id">Company</MenuItem>
              <MenuItem value="need_setup">Setup Status</MenuItem>
              <MenuItem value="demo">Demo Status</MenuItem>
              <MenuItem value="session_count">Session Count</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton
            onClick={() => handleSortChange(sortBy)}
            color="primary"
            size="small"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pagination.limit}
              label="Per Page"
              onChange={handleLimitChange}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {/* Results info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {organizations.length} of {pagination.totalCount} projects
            {appliedSearchTerm && ` for "${appliedSearchTerm}"`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sorted by: {sortBy.replace('_', ' ')} ({sortOrder})
          </Typography>
        </Box>
      </Paper>

      {/* Error State */}
      {error && (
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Table Section */}
      <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
         <TableContainer 
           component={Paper} 
           sx={{ 
             height: 'calc(100vh - 290px)', // Reduced height to show fewer rows
             borderRadius: 0,
             overflow: 'auto',
             '& .MuiTable-root': {
               tableLayout: 'fixed'
             }
           }}
         >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell 
                    key={column.key}
                    sx={{ 
                      fontWeight: 600,
                      bgcolor: 'grey.100',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      py: 1
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : organizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No projects found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {appliedSearchTerm 
                          ? `No results found for "${appliedSearchTerm}"`
                          : 'Try adjusting your search criteria'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                organizations.map((organization, index) => (
                  <TableRow 
                    key={organization.unique_id || index} 
                    hover
                    onClick={() => handleRowClick(organization)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'grey.25'
                      }
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell 
                        key={column.key}
                        sx={{ 
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          py: 0.5
                        }}
                      >
                        {renderCell(organization, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Paper elevation={1} sx={{ p: 1, borderRadius: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default Organizations; 